import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { PenLine, ArrowRight, AlertCircle, Sparkles } from "lucide-react";
import { ZOOD_LOGO } from "@/lib/brand";

export function AuthShell({ title, subtitle, children, footer }) {
  return (
    <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md rounded-[32px] border border-[#E5DECF] bg-white p-8 sm:p-10 shadow-xl"
      >
        <Link to="/" className="inline-flex">
          <img src={ZOOD_LOGO} alt="ZoodLeap" className="h-10 w-auto" data-testid="auth-logo" />
        </Link>
        <h1 className="mt-6 font-heading text-2xl font-extrabold text-[#1F2937]">{title}</h1>
        <p className="mt-1 font-body text-[#6B7280]">{subtitle}</p>
        <div className="mt-6">{children}</div>
        {footer && <div className="mt-6 text-center text-sm font-body text-[#6B7280]">{footer}</div>}
      </motion.div>
    </div>
  );
}

export function ErrorNote({ message }) {
  if (!message) return null;
  return (
    <div data-testid="auth-error" className="mb-4 flex items-center gap-2 rounded-2xl bg-[#E05A47]/10 px-4 py-3 text-sm font-semibold text-[#B4402F]">
      <AlertCircle size={16} /> {message}
    </div>
  );
}

const inputClass =
  "w-full rounded-2xl border-2 border-[#E5DECF] bg-[#FAF7F2] px-4 py-3.5 text-base font-body text-[#1F2937] outline-none transition-colors focus:border-[#0D9488]";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email.trim(), password);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      title="Welcome back"
      subtitle="Log in to your teacher, parent, or admin account."
      footer={
        <>
          Are you a student?{" "}
          <Link to="/student-login" className="font-bold text-[#0D9488] hover:underline" data-testid="go-student-login">Student login</Link>
          <div className="mt-2">
            New here?{" "}
            <Link to="/signup" className="font-bold text-[#0D9488] hover:underline">Create an account</Link>
          </div>
        </>
      }
    >
      <form onSubmit={submit}>
        <ErrorNote message={error} />
        <label className="block text-sm font-bold text-[#1F2937] mb-2">Email</label>
        <input data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} placeholder="you@school.com" autoComplete="email" />
        <label className="block text-sm font-bold text-[#1F2937] mb-2 mt-4">Password</label>
        <input data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputClass} placeholder="••••••••" autoComplete="current-password" />
        <div className="mt-2 text-right">
          <Link to="/forgot-password" data-testid="forgot-password-link" className="text-sm font-bold text-[#0D9488] hover:underline">Forgot password?</Link>
        </div>
        <button type="submit" disabled={busy} data-testid="login-submit-button" className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[#0D9488] px-6 py-4 text-lg font-bold text-white shadow-md transition-all hover:bg-[#0B7F74] active:translate-y-0.5 disabled:opacity-50">
          {busy ? "Logging in..." : "Log In"} <ArrowRight size={20} />
        </button>
      </form>
    </AuthShell>
  );
}
